import React, { type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { bindAll } from 'bind-event-listener';
import { createPortal } from 'react-dom';

import { cssMap, cx } from '@atlaskit/css';
import FeatureGates from '@atlaskit/feature-gate-js-client/feature-gates';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { Box } from '@atlaskit/primitives/compiled';
import UFOCustomCohortData from '@atlaskit/react-ufo/custom-cohort-data';
import UFOCustomData from '@atlaskit/react-ufo/custom-data';
import UFOLoadHold from '@atlaskit/react-ufo/load-hold';
import UFOSegment from '@atlaskit/react-ufo/segment';
import { token } from '@atlaskit/tokens';
import { SearchAIDialog } from '@atlassian/search-ai/ai-answer-dialog';
import { Bookmark } from '@atlassian/search-ai/bookmark';
import { onBookmarkRequestsCompleted } from '@atlassian/search-ai/bookmark-analytics';
import { BookmarkSuggestionCard } from '@atlassian/search-ai/bookmark-suggestion-card';
import {
	AiBrand,
	CardTemplate,
	QueryIntent,
	SEARCH_AI_ANSWER_CACHE_KEY,
} from '@atlassian/search-ai/constants';
import { BookmarkState } from '@atlassian/search-ai/fetch-bookmark';
import { JiraIssueSmartCard } from '@atlassian/search-ai/JiraIssueSmartCard';
import { type BookmarkIntent } from '@atlassian/search-ai/query-intent-api/types';
import { SAINFeatureControl } from '@atlassian/search-ai/sain-feature-control';
import { sainStreamFetchConvoAIService } from '@atlassian/search-ai/stream-api/stream-fetch';
import { AIAnswerAPIType, type SearchAIAnswerQueryVariables } from '@atlassian/search-ai/types';
import { useIsSearchQueryPopular } from '@atlassian/search-ai/use-is-search-query-popular';
import { useAdminPermissions } from '@atlassian/search-ai/useAdminPermissions';
import { useAnalytics } from '@atlassian/search-ai/useAnalytics';
import {
	useBookmarkQuery,
	type UseBookmarkQueryResult,
} from '@atlassian/search-ai/useBookmarkQuery';
import { isBookmarkIntent } from '@atlassian/search-ai/utils';
import { deleteSessionItem, getSessionItem } from '@atlassian/search-dialog/session-storage-utils';
import { getBackendExperiment } from '@atlassian/search-experiment/backend';
import { STATSIG_SEARCH_BACKEND_NON_OVERLAPPING_DOMAIN } from '@atlassian/search-experiment/constants';

import { EVENT_SOURCE } from '../../../common/constants/analytics';
import { ApplicationModes } from '../../../common/constants/application-modes';
import { ProductKeys } from '../../../common/constants/product-keys';
import { MapProductToKnowledgeSources } from '../../../common/constants/products';
import { SEARCH_DIALOG_AI_DIALOG_REFERER_KEY } from '../../../common/constants/quick-find';
import { ResultsStates } from '../../../common/constants/results';
import { UserStatus } from '../../../common/constants/types';
import { useAppContext } from '../../../common/ui/app-context';
import { getRovoJourneyIdFromUrl } from '../../../common/utils/get-rovo-journey-id-from-url';
import { isHello } from '../../../common/utils/is-hello';
import { usePrevious } from '../../../common/utils/use-previous/use-previous';
import { useCommonAndPrivateAttributes } from '../../../controllers/analytics';
import { useAreLLMPoweredFeaturesEnabled } from '../../../controllers/entitlement-checks';
import { ErrorBoundary } from '../../../controllers/error-boundary';
import { useSelectedFilters } from '../../../controllers/filters/selected';
import { useSearchPageProactiveAiCohort } from '../../../controllers/proactive-ai-experiment';
import { useQueryIntent } from '../../../controllers/query-intent';
import { useSmartAnswersIntentStore } from '../../../controllers/smart-answers-intent-store';
import {
	useApplicationMode,
	useBootstrap,
	usePrimaryProduct,
	useSearchSessionId,
	useUser,
} from '../../../controllers/store/bootstrap';
import {
	useResults,
	useResultsError,
	useResultsState,
	useSearchQuery,
	useSelectedProduct,
} from '../../../controllers/store/search';

import { SAIN_ENTITIES } from './constants';
import { getStoreMessageBodyParam, getStoreMessageHeader } from './utils/get-store-message-header';
import {
	mapFiltersToAggregator,
	mapSelectedFiltersToAggregator,
} from './utils/map-filters-to-aggregator';

const SainOnDemandBanner = React.lazy(() =>
	import(
		/* webpackChunkName: "sain-on-demand-banner" */
		'@atlassian/search-ai/sain-on-demand-banner'
	).then((mod) => ({
		default: mod.SainOnDemandBanner,
	})),
);

const wrapperStyles = cssMap({
	root: {
		'&:empty': {
			display: 'none',
		},
	},
	wrapper: {
		width: '100%',
		marginTop: token('space.025'),
		'&:empty': {
			display: 'none',
		},
	},
});

const smartAnswersPortalStyles = cssMap({
	root: {
		'&:empty': {
			display: 'none',
		},
	},
	hidden: {
		display: 'none',
	},
});

const floatingSmartAnswerReadyStyles = cssMap({
	root: {
		position: 'fixed',
		'&:empty': {
			display: 'none',
		},
	},
});

const FLOATING_TOP_OFFSET_PX = 135;
// The Lite filters UI is taller than the standard layout, so we need a larger top offset to ensure
// the floating SmartAnswerReady banner is not obscured by the filters bar.
const FLOATING_TOP_OFFSET_LITE_MODE_PX = 189;
const FLOATING_Z_INDEX = 10000;

type AiSearchStreamingFunctionParams = {
	variables: Omit<SearchAIAnswerQueryVariables, 'cloudIdARI'>;
};

const handleNavigateDummyFunction = () => {};

/**
 * Mirrors the render decision made later in the SmartAnswers render body:
 * will this component actually display an AI-answer-style result (Jira Issue
 * Card or smart answer card), as opposed to a bookmark / popular suggestion /
 * nothing.
 *
 * Used by the shared `isSainAnswerVisible` store flag so the auto-open Rovo
 * chat controller can suppress itself only when SAIN truly takes over.
 */
const computeWillRenderSmartAnswer = ({
	queryIntent,
	showSmartAnswer,
	showBookmark,
	bookmarkUrl,
}: {
	queryIntent: QueryIntent;
	showSmartAnswer: boolean;
	showBookmark: boolean;
	bookmarkUrl: string | undefined | null;
}): boolean => {
	if (queryIntent === QueryIntent.JIRA_ISSUE) {
		return true;
	}
	// When showSmartAnswer is true, an existing bookmark card pre-empts the
	// AI answer (SAIN component returns the bookmark card and skips the AI
	// answer render). Any other branch leaves SAIN rendering nothing
	// AI-shaped, so the auto-open chat should NOT be suppressed.
	const willRenderBookmarkInsteadOfAnswer = showBookmark && !!bookmarkUrl;
	return showSmartAnswer && !willRenderBookmarkInsteadOfAnswer;
};

const SmartAnswerDialogWrapper = ({ children }: { children?: ReactNode }) => (
	<Box xcss={wrapperStyles.wrapper}>{children}</Box>
);

/**
 * Emits a UFO custom cohort tag recording whether the SAIN intent preload
 * feature gate (triggered from Quick Find) is active for this session.
 *
 * This is rendered at a stable mount point (the top-level
 * `search-page-smart-answers` UFO segment) rather than inside the
 * `queryIntentLoading` branch, because the preload can finish before
 * SmartAnswers mounts — in which case the loading branch never renders and
 * the cohort tag would be lost for exactly the sessions we want to measure.
 */
const SainIntentPreloadCohort = () => (
	<UFOCustomCohortData
		dataKey="sainIntentPreload"
		value={fg('rovo_fps_preload_sain_intent_from_quick_find')}
	/>
);

const SainOnDemandSlot = ({
	renderDialog,
	onShown,
	onDismiss,
	onTrigger,
}: {
	renderDialog: () => React.JSX.Element;
	onShown: () => void;
	onDismiss: () => void;
	onTrigger: () => void;
}): React.JSX.Element => {
	const [triggered, setTriggered] = React.useState(false);

	if (triggered) {
		return renderDialog();
	}

	return (
		<React.Suspense fallback={null}>
			<SainOnDemandBanner
				onTrigger={() => {
					onTrigger();
					setTriggered(true);
				}}
				onShown={onShown}
				onDismiss={onDismiss}
			/>
		</React.Suspense>
	);
};

export type SmartAnswersProps = {
	isLiteMode?: boolean;
	isRelayEnabled?: boolean;
	resultsState?: ResultsStates;
	smartAnswersPortalRef?: React.RefObject<HTMLDivElement>;
	fullWidthSainPortalRef?: React.RefObject<HTMLDivElement>;
	/**
	 * Portal target for rendering the floating SmartAnswerReady when the user
	 * has scrolled the answer card out of the viewport. Provided by SmartAnswersWrapper
	 * and threaded through to SearchAIDialog → SmartAnswerCardFolded.
	 */
	floatingSmartAnswerReadyPortalRef?: React.RefObject<HTMLDivElement>;
	/** Sites in scope for the search (current site plus `?sites=` selection). */
	selectedSites?: ReadonlyMap<string, string>;
};

/**
 * Get the AiBrand based on Rovo edition, ai enablement and application mode.
 */
export const getShowChatFollowUps: ({
	isRovoLLMEnabled,
	applicationMode,
}: {
	isRovoLLMEnabled?: boolean;
	applicationMode?: ApplicationModes;
}) => boolean | undefined = ({
	isRovoLLMEnabled,
	applicationMode,
}: {
	isRovoLLMEnabled?: boolean;
	applicationMode?: ApplicationModes;
}) => {
	if (fg('rovo_search_updated_entitlement_checks_for_team_25')) {
		if (applicationMode === ApplicationModes.Unified) {
			return false;
		}
		return isRovoLLMEnabled;
	}
	return applicationMode === ApplicationModes.Rovo;
};

export const SmartAnswers: (props: SmartAnswersProps) => React.JSX.Element | null = (
	props: SmartAnswersProps,
): React.JSX.Element | null => {
	const { isAdminHubAIEnabled, isRovoLLMEnabled } = useAppContext();

	const [
		{
			cloudId,
			user,
			orgId,
			SAINHostName,
			analyticsAttributes,
			experimentConfig,
			productEdition,
			productDataRegions,
			isJiraIssueIndexed,
		},
	] = useBootstrap();
	const [searchSessionId] = useSearchSessionId();
	// Read on every render, deliberately not memoised: Quick Find stays mounted on this page, so
	// re-querying puts a new id in the URL while this component never remounts. Not `useQueryParams()`,
	// which is RSS-backed and so not allowed in `search-page-relay/` (QS-10491).
	const rovoJourneyId = getRovoJourneyIdFromUrl();
	const [query, { setSearchQuery }] = useSearchQuery();
	const [primaryProduct] = usePrimaryProduct();
	const proactiveAiCohort = useSearchPageProactiveAiCohort();
	const { hasFiltersSelected, selectedFilters } = useSelectedFilters({});
	const applicationMode = useApplicationMode();
	const [selectedProduct] = useSelectedProduct();
	const mappedProductFilter = MapProductToKnowledgeSources[selectedProduct];
	const [previousMappedProductFilter] = usePrevious(mappedProductFilter, { count: 1 });
	const [fireAnalyticsEvent] = useAnalytics();
	const {
		commonAttributes: { has3pProducts, is3pSearch, mode, availableProducts, queryHash },
	} = useCommonAndPrivateAttributes();
	const {
		clearStore: clearSearchResultStore,
		setQueryIntentClassificationData,
		setWorkspaceId,
		setSainJql,
		setIsSainAnswerVisible,
		setIsSainAnswerExpanded,
	} = useSmartAnswersIntentStore();
	// eslint-disable-next-line @atlassian/search-common/no-use-rss-hooks
	const [results] = useResults();
	// eslint-disable-next-line @atlassian/search-common/no-use-rss-hooks
	const [error] = useResultsError();
	// eslint-disable-next-line @atlassian/search-common/no-use-rss-hooks
	const resultsState = useResultsState();
	let resultsLoaded = resultsState === ResultsStates.RESULTS;

	if (props.isRelayEnabled) {
		resultsLoaded = props?.resultsState === ResultsStates.RESULTS;
	}

	const {
		loading: queryIntentLoading,
		transformedQuery,
		queryIntent,
		queryIntentClassificationData,
		queryIntentDetectionMetadata,
		requestId: queryIntentRequestId,
		traceId: queryIntentTraceId,
	} = useQueryIntent();

	// `useQueryIntent` already mirrors `queryIntent` into the shared store on
	// every classification, so no extra mirroring is needed here.

	// Mirror "is SAIN going to render an AI answer right now?" into the shared
	// store so consumers (e.g. AutoOpenRovoChatOnLanding) can suppress on
	// SAIN's authoritative render decision. The real value depends on
	// `showSmartAnswer` / `showBookmark` etc. which are computed BELOW the
	// early-return branches, so we use the standard "ref read in post-commit
	// effect" pattern: declare the ref + effect here at the top (always
	// called, satisfies Rules of Hooks), let the render body update the ref
	// before commit, and reset to false on unmount so a late unmount doesn't
	// leave the store stuck at true.
	//
	// TODO: consolidate this with SAIN's own render decision instead of
	// using the ref-in-render + post-commit-effect mirror pattern. Reviewer
	// concern (PR #384319): "I'm wondering if the same could be achieved
	// in a cleaner way by consolidating the existing logic in SAIN (there
	// must [be] logic already deciding if the component should be
	// rendered), instead of simply waiting for the component to render in
	// order to set this value. Looks a little hacky."
	const willRenderSmartAnswerRef = useRef(false);
	willRenderSmartAnswerRef.current = false;
	useEffect(() => {
		setIsSainAnswerVisible(willRenderSmartAnswerRef.current);
	});
	useEffect(
		() => () => {
			setIsSainAnswerVisible(false);
			setIsSainAnswerExpanded(false);
		},
		[setIsSainAnswerVisible, setIsSainAnswerExpanded],
	);

	const cloudIdARI = `ari:cloud:confluence::site/${cloudId}`;
	const workspaceId = analyticsAttributes?.activationId
		? `ari:cloud:${primaryProduct}:${cloudId}:workspace/${analyticsAttributes.activationId}`
		: '';
	const xProduct = applicationMode === ApplicationModes.Rovo ? 'rovo' : undefined;

	// Get dialog referrer tracking from sessionStorage
	const dialogReferrer = getSessionItem(SEARCH_DIALOG_AI_DIALOG_REFERER_KEY);

	// Clear the dialog referrer tracking after analytics events are fired
	const handleAnalyticsFired = useCallback(() => {
		// Clear the dialog referrer from sessionStorage
		deleteSessionItem(SEARCH_DIALOG_AI_DIALOG_REFERER_KEY);
	}, []);

	let showBookmark: boolean = false;
	let bookmarkResult: Omit<UseBookmarkQueryResult, 'refetch'> = {
		bookmarkState: 'ACTIVE' as BookmarkState,
		url: '',
		loading: false,
		id: '',
		isError: false,
		title: '',
		description: undefined,
		lastModifierAccountId: '',
		keyPhrase: '',
		parentAdminhubBookmarkId: '',
		requestId: null,
	};

	const bookmarkAPIBookmark = useBookmarkQuery({
		cloudId: cloudId || '',
		query,
		workspaceId,
		product: primaryProduct || '',
		applicationMode,
	});
	let queryIntentBookmark: BookmarkIntent['bookmark'] | undefined;
	if (
		queryIntentClassificationData &&
		isBookmarkIntent(queryIntentClassificationData) &&
		fg('kd_bookmark_query_intent')
	) {
		queryIntentBookmark = queryIntentClassificationData.bookmark;
	}

	const normalizeIntentBookmark = (
		bookmark: BookmarkIntent['bookmark'],
	): Omit<UseBookmarkQueryResult, 'refetch'> => ({
		id: bookmark.id || '',
		url: bookmark.url || '',
		title: bookmark.title || '',
		description: bookmark.description || '',
		keyPhrase: bookmark.keyPhrase ?? '',
		bookmarkState: (Object.values(BookmarkState).includes(bookmark.bookmarkState as BookmarkState)
			? bookmark.bookmarkState
			: BookmarkState.ACTIVE) as BookmarkState,
		lastModifierAccountId: bookmark.lastModifierAccountId ?? '',
		parentAdminhubBookmarkId: bookmark.parentAdminhubBookmarkId ?? '',
		// Resolved defaults not present on intent type
		loading: false,
		isError: false,
		requestId: queryIntentRequestId ?? null,
	});

	const normalizedIntentBookmark = queryIntentBookmark
		? normalizeIntentBookmark(queryIntentBookmark)
		: undefined;

	const areBookmarksEqual = (): boolean => {
		if (
			!normalizedIntentBookmark?.id ||
			!bookmarkAPIBookmark.id ||
			bookmarkAPIBookmark.loading ||
			bookmarkAPIBookmark.isError
		) {
			return false;
		}
		return (
			normalizedIntentBookmark.id === bookmarkAPIBookmark.id &&
			normalizedIntentBookmark.url === bookmarkAPIBookmark.url &&
			normalizedIntentBookmark.title === bookmarkAPIBookmark.title &&
			normalizedIntentBookmark.description === bookmarkAPIBookmark.description &&
			normalizedIntentBookmark.keyPhrase === bookmarkAPIBookmark.keyPhrase &&
			normalizedIntentBookmark.bookmarkState === bookmarkAPIBookmark.bookmarkState &&
			normalizedIntentBookmark.lastModifierAccountId ===
				bookmarkAPIBookmark.lastModifierAccountId &&
			normalizedIntentBookmark.parentAdminhubBookmarkId ===
				bookmarkAPIBookmark.parentAdminhubBookmarkId
		);
	};

	const bookmarksAreEqual = areBookmarksEqual();

	const shouldUseIntentBookmark = (): boolean => {
		if (!normalizedIntentBookmark?.id || bookmarkAPIBookmark.loading) {
			return false;
		}
		if (!bookmarkAPIBookmark.id || bookmarkAPIBookmark.isError) {
			// API resolved without a bookmark — trust intent
			return true;
		}
		return bookmarksAreEqual;
	};

	if (shouldUseIntentBookmark() && fg('kd_bookmark_query_intent')) {
		bookmarkResult = normalizedIntentBookmark!;
	} else {
		bookmarkResult = bookmarkAPIBookmark;
	}

	const queryIntentHasBookmark = !!queryIntentBookmark;
	const queryIntentBookmarkId = queryIntentBookmark?.id;
	const bookmarkAPIHasBookmark = !!bookmarkAPIBookmark.url && !bookmarkAPIBookmark.isError;

	const hasLoggedParityEventRef = useRef(false);
	useEffect(() => {
		hasLoggedParityEventRef.current = false;
	}, [query]);

	useEffect(() => {
		if (!fg('kd_bookmark_query_intent')) {
			return;
		}
		if (bookmarkAPIBookmark.loading || queryIntentLoading) {
			return;
		}
		if (!hasLoggedParityEventRef.current && (queryIntentHasBookmark || bookmarkAPIHasBookmark)) {
			hasLoggedParityEventRef.current = true;
			const event = onBookmarkRequestsCompleted({
				searchSessionId,
				queryHash,
				source: EVENT_SOURCE,
				bookmarkAPIHasBookmark,
				queryIntentHasBookmark,
				bookmarkAPIBookmarkId: bookmarkAPIBookmark.id || undefined,
				queryIntentBookmarkId: queryIntentBookmarkId || undefined,
				bookmarkAPIRequestId: bookmarkAPIBookmark.requestId,
				queryIntentRequestId: queryIntentRequestId ?? null,
				bookmarksAreEqual,
			});
			fireAnalyticsEvent(event);
		}
	}, [
		bookmarkAPIBookmark.loading,
		bookmarkAPIBookmark.id,
		bookmarkAPIBookmark.requestId,
		bookmarkAPIHasBookmark,
		bookmarksAreEqual,
		fireAnalyticsEvent,
		query,
		queryHash,
		queryIntentBookmarkId,
		queryIntentHasBookmark,
		queryIntentLoading,
		queryIntentRequestId,
		searchSessionId,
	]);

	const { hasBookmarkCreationAccess, hasAdminhubAccess } = useAdminPermissions({
		cloudId: cloudId || '',
		orgId: orgId || '',
	});
	const {
		isPopular: isSearchQueryPopular,
		loading: isSearchQueryPopularLoading,
		error: isSearchQueryPopularError,
	} = useIsSearchQueryPopular({
		cloudId: cloudId ?? '',
		searchQuery: query,
		isEnabled: fg('kd_user_created_bookmarks'),
	});

	// if SAIN nav card is enabled, we show the nav card if the query intent is NAV_CONTENT or NAVIGATIONAL
	// for NAVIGATIONAL intents, we use the nav boost score from the FPS search results, which is checked inside the store
	const showNavCard =
		(queryIntent === QueryIntent.NAV_CONTENT || queryIntent === QueryIntent.NAVIGATIONAL) &&
		fg('kd_enable_sain_hero_nav');

	const parsedFilters = fg('kd_add_common_filters_support_to_sain')
		? // eslint-disable-next-line react-hooks/rules-of-hooks
			useMemo(() => mapSelectedFiltersToAggregator(selectedFilters), [selectedFilters])
		: // eslint-disable-next-line react-hooks/rules-of-hooks
			useMemo(
				() => mapFiltersToAggregator(selectedFilters.confluenceFilters),
				[selectedFilters.confluenceFilters],
			);

	useEffect(() => {
		// clear the search result store when the query changes or component unmounts
		// this prevents stale nav card data from persisting when transitioning between intents
		fg('kd_enable_sain_hero_nav') && clearSearchResultStore();
		return () => {
			fg('kd_enable_sain_hero_nav') && clearSearchResultStore();
		};
	}, [query, clearSearchResultStore]);

	if (fg('kd_hide_sain_for_unsupported_product_filters')) {
		// fg check is stable between renders and won't cause issues with hook calls
		// eslint-disable-next-line react-hooks/rules-of-hooks
		useEffect(() => {
			if (previousMappedProductFilter && previousMappedProductFilter !== mappedProductFilter) {
				deleteSessionItem(SEARCH_AI_ANSWER_CACHE_KEY);
			}
		}, [previousMappedProductFilter, mappedProductFilter]);
	}

	const tracingHeaders = useMemo(() => {
		// Use trace headers from intent detection, use arbitraty span id since its needed to propagate trace id
		if (queryIntentTraceId) {
			const spanId = crypto.randomUUID().replace(/-/g, '').slice(0, 16);
			return {
				'X-B3-TraceId': queryIntentTraceId,
				'X-B3-SpanId': spanId,
			};
		}
		return undefined;
	}, [queryIntentTraceId]);
	const requestId = tracingHeaders?.['X-B3-TraceId'];

	// A callback needed to parse the streaming response, ideally it should be defined inside @atlassian/search-ai
	const aiSearchStreamingFunction = useCallback(
		({ variables }: AiSearchStreamingFunctionParams) => {
			return {
				type: AIAnswerAPIType.STREAM,
				stream: sainStreamFetchConvoAIService({
					variables: { ...variables, cloudIdARI },
					fetchConfig: {
						hostName: SAINHostName,
						credentials: 'include',
						headers: {
							'X-Cloudid': cloudId || '',
							...(xProduct ? { 'X-Product': xProduct } : {}),
							...(tracingHeaders ?? {}),
							...getStoreMessageHeader(),
						},
					},
					storeMessage: getStoreMessageBodyParam(),
					isReadingAids: false,
				}),
			};
		},
		[SAINHostName, cloudIdARI, cloudId, xProduct, tracingHeaders],
	);

	const backendExperiment = useMemo(
		() =>
			getBackendExperiment({
				experimentConfig,
				entities: SAIN_ENTITIES,
				productEdition,
				isHello: isHello(cloudId),
				productDataRegions,
				experience: 'sain',
				tenantId: cloudId,
				isJiraIssueIndexed,
				thirdPartyFilters: selectedFilters.thirdPartyFilters,
			}),
		[
			experimentConfig,
			productEdition,
			cloudId,
			productDataRegions,
			isJiraIssueIndexed,
			selectedFilters,
		],
	);
	const { experimentLayers } = backendExperiment || {};

	const extraAnalyticsAttributes = useMemo(
		() => ({
			isFullPageSearch: true,
			has3pProducts,
			is3pSearch,
			mode,
			availableProducts,
			referrer: dialogReferrer,
			originalQuery: query,
			transformedQuery: transformedQuery,
			requestId,
		}),
		[
			has3pProducts,
			is3pSearch,
			mode,
			availableProducts,
			dialogReferrer,
			query,
			transformedQuery,
			requestId,
		],
	);

	const sainDialogCallbacks = useMemo(
		() => ({
			onAIAnswerAnalyticsFired: () => handleAnalyticsFired(),
			onSainJqlResolved: setSainJql,
		}),
		[handleAnalyticsFired, setSainJql],
	);

	const userDetail = useMemo(() => ({ id: user?.id || '', name: user?.name || '' }), [user]);

	if (
		props.isRelayEnabled &&
		(props.resultsState === ResultsStates.NO_RESULTS || props.resultsState === ResultsStates.ERROR)
	) {
		return null;
	} else if (error && !results) {
		// Do not show smart answers when there are errors, or empty results
		return null;
	}

	// Do not show smart answers when the required data is not available
	if (!cloudId || !query) {
		return null;
	}

	// Do not show smart answers or bookmarks if query intent isn't set or is loading
	if (queryIntent === QueryIntent.DEFAULT || queryIntentLoading) {
		// If loading, register a UFO hold
		if (queryIntentLoading) {
			return <UFOLoadHold name="query-intent" />;
		}
		return null;
	}

	// if the SAIN nav card is enabled,
	// we rely on the intent detection API to determine if we should show the bookmark component
	// this prevents both the nav card and bookmark from being shown at the same time,
	// since there's a high overlap for these two components
	if (fg('kd_enable_sain_hero_nav')) {
		showBookmark = Boolean(
			queryIntent === QueryIntent.BOOKMARK &&
			queryIntentClassificationData &&
			isBookmarkIntent(queryIntentClassificationData) &&
			!selectedProduct &&
			resultsLoaded,
		);
	} else {
		showBookmark =
			!bookmarkResult.loading &&
			!bookmarkResult.isError &&
			queryIntent !== QueryIntent.PERSON &&
			!selectedProduct &&
			resultsLoaded;
	}

	if (showNavCard && queryIntentClassificationData) {
		setWorkspaceId(workspaceId ?? '');
		setQueryIntentClassificationData(queryIntentClassificationData);
	}

	const showPopularQuerySuggestion =
		isSearchQueryPopular &&
		!isSearchQueryPopularLoading &&
		!isSearchQueryPopularError &&
		showBookmark &&
		(queryIntent === QueryIntent.NAVIGATIONAL || queryIntent === QueryIntent.KEYWORD_OR_ACRONYM) &&
		fg('kd_user_created_bookmarks');

	const aiBrand = applicationMode === ApplicationModes.Rovo ? AiBrand.ROVO : AiBrand.AI;

	const hideTopicCard = queryIntent === QueryIntent.TOPIC && !!bookmarkResult.url;

	// No need to check for loaded results before showing SAIN (decision made by PM and team)
	const showSmartAnswer =
		queryIntent !== QueryIntent.NONE &&
		queryIntent !== QueryIntent.NAV_CONTENT &&
		(queryIntent !== QueryIntent.NAVIGATIONAL ||
			(queryIntent === QueryIntent.NAVIGATIONAL &&
				SAINFeatureControl.isNavigationalIntentEnabled())) &&
		queryIntent !== QueryIntent.BOOKMARK &&
		!bookmarkResult.loading &&
		!hideTopicCard &&
		(fg('kd_hide_sain_for_unsupported_product_filters')
			? // check either if no product filter is selected or a supported product filter is selected
				(!selectedProduct || mappedProductFilter !== undefined) && !hasFiltersSelected
			: true);

	// Bookmark / popular-query suggestions do NOT count as a SAIN answer —
	// the auto-open Rovo chat is allowed to coexist with those.
	willRenderSmartAnswerRef.current = computeWillRenderSmartAnswer({
		queryIntent,
		showSmartAnswer,
		showBookmark,
		bookmarkUrl: bookmarkResult.url,
	});

	// Show Jira Issue Card with priority for JIRA_ISSUE query intent
	if (queryIntent === QueryIntent.JIRA_ISSUE) {
		return (
			<UFOSegment name="search-ai-dialog">
				{fg('platform_ufo_custom_cohort_data_migration') ? (
					<>
						<UFOCustomCohortData dataKey="hasSAINComponent" value={true} />
						<UFOCustomCohortData dataKey="sainExperienceName" value="Jira Issue Card" />
					</>
				) : (
					<UFOCustomData data={{ hasSAINComponent: true, sainExperienceName: 'Jira Issue Card' }} />
				)}
				<SmartAnswerDialogWrapper>
					<JiraIssueSmartCard
						issueKeyQuery={query}
						cloudId={cloudId}
						aiBrand={aiBrand}
						searchSessionId={searchSessionId}
						workspaceId={workspaceId}
						product={primaryProduct ?? ''}
						source={EVENT_SOURCE}
					/>
				</SmartAnswerDialogWrapper>
			</UFOSegment>
		);
	}

	// If a bookmark exists & can be shown, prioritize showing bookmark only
	// If a bookmark doesn't exist & query isn't answerable by SAIN, show the bookmark component to admins
	if (
		((showBookmark && !!bookmarkResult.url) ||
			(!showSmartAnswer && showBookmark && hasBookmarkCreationAccess)) &&
		!fg('kd_team_26_sain_hide_bookmark')
	) {
		return (
			<UFOSegment name="search-ai-dialog">
				{fg('platform_ufo_custom_cohort_data_migration') ? (
					<>
						<UFOCustomCohortData dataKey="hasSAINComponent" value={true} />
						<UFOCustomCohortData dataKey="sainExperienceName" value="Bookmark" />
					</>
				) : (
					<UFOCustomData data={{ hasSAINComponent: true, sainExperienceName: 'Bookmark' }} />
				)}
				<Bookmark
					query={query}
					cloudId={cloudId}
					bookmarkId={bookmarkResult.id}
					bookmarkState={bookmarkResult.bookmarkState}
					url={bookmarkResult.url}
					orgId={orgId || ''}
					searchSessionId={searchSessionId}
					source={EVENT_SOURCE}
					brand={aiBrand}
					title={bookmarkResult.title || ''}
					description={bookmarkResult.description || ''}
					hasAdminhubAccess={hasAdminhubAccess}
					hasBookmarkCreationAccess={hasBookmarkCreationAccess}
					userDetails={userDetail}
					product={primaryProduct}
					workspaceId={workspaceId}
					lastModifierAccountId={bookmarkResult.lastModifierAccountId}
					keyPhrase={bookmarkResult.keyPhrase}
					parentAdminhubBookmarkId={bookmarkResult.parentAdminhubBookmarkId}
					requestId={bookmarkResult.requestId}
				/>
			</UFOSegment>
		);
	}

	if (showPopularQuerySuggestion && !fg('kd_team_26_sain_hide_bookmark')) {
		return (
			<BookmarkSuggestionCard
				suggestedTerm={query}
				cloudId={cloudId}
				orgId={orgId ?? ''}
				hasAdminhubAccess={hasAdminhubAccess}
				product={primaryProduct ?? 'unknown'}
				workspaceId={workspaceId}
				userDetails={userDetail}
				searchSessionId={searchSessionId}
				source={EVENT_SOURCE}
			/>
		);
	}

	const sharedSainDialogProps = {
		query: transformedQuery || query,
		cloudId,
		orgId,
		getAIAnswer: aiSearchStreamingFunction,
		onNavigate: handleNavigateDummyFunction,
		userDetails: userDetail,
		source: EVENT_SOURCE,
		wrapper: SmartAnswerDialogWrapper,
		loadingWrapper: SmartAnswerDialogWrapper,
		setQuery: setSearchQuery,
		followUpsEnabled: true as const,
		searchSessionId,
		rovoJourneyId,
		selectedFilters,
		parsedFilters,
		selectedProduct,
		mappedProductFilter,
		cardTemplate: CardTemplate.SMART_ANSWER,
		baseQuery: query,
		brand: aiBrand,
		callbacks: sainDialogCallbacks,
		workspaceId,
		isAdminHubAIEnabled,
		sourceProduct: primaryProduct,
		showChatFollowUps: getShowChatFollowUps({ isRovoLLMEnabled, applicationMode }),
		queryIntentClassificationData,
		queryIntentDetectionMetadata,
		queryIntentDetectionTraceId: queryIntentTraceId,
		...(fg('kd_sain_backend_exp_context')
			? {
					experimentContext: {
						experimentLayers: (experimentLayers || []).map(
							({ name, layerId, shadowId, definitions }) => ({
								name,
								layerId,
								shadowId,
								definitions,
							}),
						),
					},
				}
			: {}),
		referrer: dialogReferrer,
		traceHeaders: tracingHeaders,
		floatingSmartAnswerReadyPortalRef: props.floatingSmartAnswerReadyPortalRef,
		onAnswerExpandedStateChange: setIsSainAnswerExpanded,
	};

	// Otherwise if SAIN can answer a query, show the SAIN component
	if (showSmartAnswer) {
		const sainContent = (
			<>
				{showBookmark && hasBookmarkCreationAccess && !fg('kd_team_26_sain_hide_bookmark') ? (
					<Bookmark
						query={query}
						cloudId={cloudId}
						bookmarkId={bookmarkResult.id}
						url={bookmarkResult.url}
						orgId={orgId || ''}
						searchSessionId={searchSessionId}
						source={EVENT_SOURCE}
						brand={aiBrand}
						title={bookmarkResult.title || ''}
						description={bookmarkResult.description || ''}
						hasAdminhubAccess={hasAdminhubAccess}
						hasBookmarkCreationAccess={hasBookmarkCreationAccess}
						product={primaryProduct}
						workspaceId={workspaceId}
						lastModifierAccountId={bookmarkResult.lastModifierAccountId}
						keyPhrase={bookmarkResult.keyPhrase}
						parentAdminhubBookmarkId={bookmarkResult.parentAdminhubBookmarkId}
						requestId={bookmarkResult.requestId}
					/>
				) : null}
				<SearchAIDialog
					{...sharedSainDialogProps}
					extraAnalyticsAttributes={extraAnalyticsAttributes}
					smartAnswersRoute={queryIntent}
				/>
			</>
		);

		if (
			(queryIntent === QueryIntent.JIRA_NATURAL_LANGUAGE_QUERY ||
				queryIntent === QueryIntent.JIRA_NLQ_KEYWORD) &&
			primaryProduct === 'jira' &&
			props.fullWidthSainPortalRef?.current
		) {
			return createPortal(
				sainContent,
				props.fullWidthSainPortalRef.current,
				'search-page-full-width-jira-nl-sain',
			);
		}

		return <>{sainContent}</>;
	}

	const isSainOnDemandEligibleIntent =
		queryIntent === QueryIntent.NONE || queryIntent === QueryIntent.NAVIGATIONAL;

	const isSainOnDemandBannerEnabled =
		resultsLoaded &&
		isSainOnDemandEligibleIntent &&
		(proactiveAiCohort === 'on_demand_sain_banner' || fg('kd_sain_on_demand_banner'));

	if (isSainOnDemandBannerEnabled) {
		return (
			<UFOSegment name="search-page-sain-on-demand-banner">
				<SainOnDemandSlot
					key={searchSessionId}
					onShown={() =>
						fireAnalyticsEvent({
							eventType: 'track',
							actionSubject: 'sainOnDemandBanner',
							action: 'shown',
							attributes: {
								additionalAttributes: {
									searchSessionId,
									queryHash,
									queryIntent,
									proactiveAiCohort,
									queryIntentRequestId,
								},
							},
						})
					}
					onDismiss={() =>
						fireAnalyticsEvent({
							eventType: 'ui',
							actionSubject: 'button',
							actionSubjectId: 'sainOnDemandBannerDismissed',
							action: 'clicked',
							attributes: {
								additionalAttributes: {
									searchSessionId,
									queryHash,
									queryIntent,
									proactiveAiCohort,
									queryIntentRequestId,
								},
							},
						})
					}
					onTrigger={() =>
						fireAnalyticsEvent({
							eventType: 'ui',
							actionSubject: 'button',
							actionSubjectId: 'sainOnDemandBannerGenerateAnswer',
							action: 'clicked',
							attributes: {
								additionalAttributes: {
									searchSessionId,
									queryHash,
									queryIntent,
									proactiveAiCohort,
									queryIntentRequestId,
								},
							},
						})
					}
					renderDialog={() => {
						// Use the original query for the on-demand banner if the query intent is NONE, otherwise use the transformed query
						// to prevent broken query transformations being passed through (eg "No intent detected by any strategy")
						const onDemandQuery =
							queryIntent === QueryIntent.NONE ? query : (transformedQuery ?? query);
						return (
							<SearchAIDialog
								{...sharedSainDialogProps}
								query={onDemandQuery}
								extraAnalyticsAttributes={{
									...extraAnalyticsAttributes,
									triggeredBy: 'sain_on_demand_banner',
								}}
								smartAnswersRoute={QueryIntent.NATURAL_LANGUAGE_QUERY}
								userGeneratedAI
							/>
						);
					}}
				/>
			</UFOSegment>
		);
	}

	return null;
};

export const StandaloneBookmark: (props: SmartAnswersProps) => React.JSX.Element | null = (
	props: SmartAnswersProps,
): React.JSX.Element | null => {
	const [{ cloudId, user, orgId, analyticsAttributes }] = useBootstrap();
	const [searchSessionId] = useSearchSessionId();
	const [query] = useSearchQuery();
	const [primaryProduct] = usePrimaryProduct();
	const applicationMode = useApplicationMode();
	const workspaceId = analyticsAttributes?.activationId
		? `ari:cloud:${primaryProduct}:${cloudId}:workspace/${analyticsAttributes.activationId}`
		: '';

	const [selectedProduct] = useSelectedProduct();
	// eslint-disable-next-line @atlassian/search-common/no-use-rss-hooks
	const [results] = useResults();
	// eslint-disable-next-line @atlassian/search-common/no-use-rss-hooks
	const [error] = useResultsError();
	// eslint-disable-next-line @atlassian/search-common/no-use-rss-hooks
	const resultsState = useResultsState();
	let resultsLoaded = resultsState === ResultsStates.RESULTS;

	if (props.isRelayEnabled) {
		resultsLoaded = props.resultsState === ResultsStates.RESULTS;
	}

	const {
		bookmarkState,
		url: bookmarkUrl,
		loading: bookmarkLoading,
		id: bookmarkId,
		isError,
		title,
		description,
		lastModifierAccountId,
		keyPhrase,
		parentAdminhubBookmarkId,
		requestId: bookmarkRequestId,
	} = useBookmarkQuery({
		cloudId: cloudId || '',
		query,
		workspaceId,
		product: primaryProduct || '',
		isStandaloneBookmark: true,
		applicationMode,
	});
	const { hasBookmarkCreationAccess, hasAdminhubAccess } = useAdminPermissions({
		cloudId: cloudId || '',
		orgId: orgId || '',
	});

	const userDetail = useMemo(() => ({ id: user?.id || '', name: user?.name || '' }), [user]);

	if (
		props.isRelayEnabled &&
		(props.resultsState === ResultsStates.NO_RESULTS || props.resultsState === ResultsStates.ERROR)
	) {
		return null;
	} else if (error && !results) {
		// Do not show smart answers when there are errors, or empty results
		return null;
	}
	// Do not show smart answers when the required data is not available
	if (!cloudId || !query) {
		return null;
	}

	const showBookmark = !bookmarkLoading && !isError && !selectedProduct && resultsLoaded;

	// If a bookmark exists & can be shown, prioritize showing bookmark only
	// If a bookmark doesn't exist & query isn't answerable by SAIN, show the bookmark component to admins
	if (
		((showBookmark && !!bookmarkUrl) || (showBookmark && hasBookmarkCreationAccess)) &&
		!fg('kd_team_26_sain_hide_bookmark')
	) {
		return (
			<UFOSegment name="search-ai-dialog">
				{fg('platform_ufo_custom_cohort_data_migration') ? (
					<>
						<UFOCustomCohortData dataKey="hasSAINComponent" value={true} />
						<UFOCustomCohortData dataKey="sainExperienceName" value="Bookmark" />
					</>
				) : (
					<UFOCustomData data={{ hasSAINComponent: true, sainExperienceName: 'Bookmark' }} />
				)}
				<Bookmark
					query={query}
					cloudId={cloudId}
					bookmarkId={bookmarkId}
					bookmarkState={bookmarkState}
					url={bookmarkUrl}
					orgId={orgId || ''}
					searchSessionId={searchSessionId}
					source={EVENT_SOURCE}
					brand={applicationMode === ApplicationModes.Rovo ? AiBrand.ROVO : AiBrand.AI}
					title={title || ''}
					description={description || ''}
					hasAdminhubAccess={hasAdminhubAccess}
					hasBookmarkCreationAccess={hasBookmarkCreationAccess}
					userDetails={userDetail}
					product={primaryProduct}
					workspaceId={workspaceId}
					lastModifierAccountId={lastModifierAccountId}
					keyPhrase={keyPhrase}
					parentAdminhubBookmarkId={parentAdminhubBookmarkId}
					requestId={bookmarkRequestId}
				/>
			</UFOSegment>
		);
	}

	return null;
};

export const SmartAnswersIfEligible: (props: SmartAnswersProps) => React.JSX.Element | null = (
	props: SmartAnswersProps,
): React.JSX.Element | null => {
	const [userWithStatus] = useUser();
	const [primaryProduct] = usePrimaryProduct();
	const [query] = useSearchQuery();
	const { isAdminHubAIEnabled } = useAppContext();
	const applicationMode = useApplicationMode();
	const areLLMFeaturesEnabled = useAreLLMPoweredFeaturesEnabled();
	const [{ cloudId }] = useBootstrap();

	const isUserAuthenticated = userWithStatus.status === UserStatus.Authenticated;
	const isPrimaryProductEligible =
		primaryProduct === ProductKeys.Confluence ||
		primaryProduct === ProductKeys.Atlas ||
		primaryProduct === ProductKeys.Jira ||
		primaryProduct === ProductKeys.Loom;
	// SAIN renders in SearchPageLite using the same eligibility rules as outside of
	// lite mode (i.e. regardless of which app pill is selected). The `lite` runtime
	// feature already scopes the SearchPageLite variant, so no extra platform
	// feature gate is needed here.
	if (!isUserAuthenticated || !isPrimaryProductEligible || !query) {
		return null;
	}

	// SAIN answers are scoped to a single site, so hide it when searching across
	// multiple sites.
	if ((props.selectedSites?.size ?? 0) > 1) {
		return null;
	}

	// Experiment to disable SAIN based on: https://atlassian.slack.com/archives/C06K9T53N66/p1757961269661659?thread_ts=1754004619.291609&cid=C06K9T53N66
	// skip hello users for SAIN holdback exp
	if (!isHello(cloudId) && fg('kd_disable_sain_experiment_fg')) {
		const layer = FeatureGates.getLayer(STATSIG_SEARCH_BACKEND_NON_OVERLAPPING_DOMAIN);
		const abTestId = layer.get<string | null>('abTestId', 'no_ab_test_id');
		if (abTestId === 'kd_disable_sain_experiment') {
			const backendExperimentData = layer.get<{ sain: boolean } | null>('backendChanges', {
				sain: true,
			});
			if (!backendExperimentData?.sain) {
				return null;
			}
		}
	}

	if (fg('rovo_search_updated_entitlement_checks_for_team_25')) {
		if (areLLMFeaturesEnabled) {
			return (
				<UFOSegment name="search-page-smart-answers">
					<SainIntentPreloadCohort />
					<SmartAnswers {...props} />
				</UFOSegment>
			);
		}

		return (
			<UFOSegment name="search-page-smart-answers">
				<SainIntentPreloadCohort />
				<StandaloneBookmark {...props} />
			</UFOSegment>
		);
	} else {
		if (isAdminHubAIEnabled || applicationMode === ApplicationModes.Rovo) {
			return (
				<UFOSegment name="search-page-smart-answers">
					<SainIntentPreloadCohort />
					<SmartAnswers {...props} />
				</UFOSegment>
			);
		}
	}

	return null;
};

/**
 * A wrapper for the entire smart answers component in the search page,
 * which wraps the component in a div with a specific id. This is used
 * to calculate VC90 of the search page ignoring smart answers by filtering
 * out mutations that occur on descendants of this div during VC90 calculation.
 *
 * The specific metric this is used for is:
 * https://tools.atlassian.com/glance/experience/confluence.fe.page-load.ufo-search-page-ignoring-smart-answers
 */
const SmartAnswersWrapper = ({
	children,
	wrapperRef,
}: React.PropsWithChildren<{ wrapperRef?: React.Ref<HTMLDivElement> }>) => (
	<Box
		id="search-page-smart-answers"
		testId="search-page-smart-answers"
		xcss={wrapperStyles.root}
		ref={wrapperRef}
	>
		{children}
	</Box>
);

const SmartAnswersWithErrorBoundary = (props: SmartAnswersProps): React.JSX.Element => {
	const isUserFlowUpdatesEnabled = SAINFeatureControl.isSAINUpdatedFlowEnabled();
	const isScrollUpEnabled = SAINFeatureControl.isSAINScrollUpEnabled();
	// The floating SmartAnswerReady "scroll up" functionality ships either as part of the
	// broader updated-flow experience OR via the standalone scroll-up gate, so it can be
	// rolled out independently.
	const isScrollUpFeatureEnabled = isUserFlowUpdatesEnabled || isScrollUpEnabled;

	const floatingSmartAnswerReadyPortalRef = useRef<HTMLDivElement>(null);
	// Measures the SAIN column's horizontal position/width. Attached directly to
	// the SmartAnswersWrapper, which is a normal block box and reports an accurate
	// getBoundingClientRect.
	const wrapperRef = useRef<HTMLDivElement>(null);

	// Track the SAIN column's horizontal position/width so the floating
	// SmartAnswerReady stays aligned with the width and position of the SAIN dialog
	const [wrapperRect, setWrapperRect] = useState<{ left: number; width: number } | null>(null);

	useEffect(() => {
		if (!isScrollUpFeatureEnabled) {
			return;
		}
		const node = wrapperRef.current;
		if (!node) {
			return;
		}
		const update = () => {
			const rect = node.getBoundingClientRect();
			// Guard against transient zero-width measurements (e.g. during layout
			// thrash or when the column is momentarily detached) so we don't crush
			// the floating banner.
			if (rect.width === 0) {
				return;
			}
			setWrapperRect({ left: rect.left, width: rect.width });
		};
		update();

		const observer = new ResizeObserver(update);
		observer.observe(node);
		const unbindResize = bindAll(window, [{ type: 'resize', listener: update }]);
		return () => {
			observer.disconnect();
			unbindResize();
		};
	}, [isScrollUpFeatureEnabled]);

	const floatingPortalStyle = useMemo<React.CSSProperties>(
		() => ({
			top: `${props.isLiteMode ? FLOATING_TOP_OFFSET_LITE_MODE_PX : FLOATING_TOP_OFFSET_PX}px`,
			zIndex: FLOATING_Z_INDEX,
			left: wrapperRect ? `${wrapperRect.left}px` : undefined,
			width: wrapperRect ? `${wrapperRect.width}px` : undefined,
		}),
		[wrapperRect, props.isLiteMode],
	);

	return (
		<ErrorBoundary section="SearchPageSmartAnswers">
			<SmartAnswersWrapper wrapperRef={wrapperRef}>
				{fg('kd_enable_sain_hero_nav') && (
					<Box
						xcss={cx(
							smartAnswersPortalStyles.root,
							// Hide the SAIN portal when the results state is not RESULTS
							// This is to prevent the SAIN portal from showing the previous nav card
							props.resultsState !== ResultsStates.RESULTS && smartAnswersPortalStyles.hidden,
						)}
						ref={props.smartAnswersPortalRef}
						testId="sain-nav-card-portal"
					/>
				)}
				{isScrollUpFeatureEnabled && (
					<Box
						xcss={floatingSmartAnswerReadyStyles.root}
						// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop
						style={floatingPortalStyle}
						ref={floatingSmartAnswerReadyPortalRef}
						testId="sain-floating-ready-portal"
					/>
				)}
				<SmartAnswersIfEligible
					{...props}
					floatingSmartAnswerReadyPortalRef={
						isScrollUpFeatureEnabled ? floatingSmartAnswerReadyPortalRef : undefined
					}
				/>
			</SmartAnswersWrapper>
		</ErrorBoundary>
	);
};
export default SmartAnswersWithErrorBoundary;
